import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowUpRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "./Reveal";
import { PaintSplat } from "./PaintSplat";
import { Magnetic } from "./Magnetic";

const EMAIL = "bigdogs@silverschnauzerstudios.com";
const API = process.env.REACT_APP_BACKEND_URL;
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

const FIELD =
  "w-full bg-transparent border-b border-[#2a2a2a] py-3 text-[#eaeaea] placeholder-[#5a5a5a] " +
  "outline-none transition-colors duration-300 focus:border-[#2dd4bf]";

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "", website: "" });
  const [sending, setSending] = useState(false);
  const mountedAt = useRef(Date.now());
  const formRef = useRef(form);
  formRef.current = form;
  const widgetId = useRef(null);
  const settled = useRef(true); // guards one submission per attempt

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Actual POST — receives a fresh Turnstile token (or null when not configured).
  const finishSubmit = useCallback(async (token) => {
    if (settled.current) return; // already handled this attempt
    settled.current = true;
    const f = formRef.current;
    try {
      const res = await fetch(`${API}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: f.name.trim(),
          email: f.email.trim(),
          company: f.company.trim() || null,
          message: f.message.trim(),
          website: f.website,
          elapsed_ms: Date.now() - mountedAt.current,
          turnstile_token: token || null,
        }),
      });
      if (!res.ok) throw new Error("Request failed");
      toast.success("Thanks — your message is on its way. We'll be in touch soon.");
      setForm({ name: "", email: "", company: "", message: "", website: "" });
    } catch {
      toast.error("Something went wrong. Please email us directly instead.");
    } finally {
      setSending(false);
      if (widgetId.current !== null && window.turnstile) {
        try { window.turnstile.reset(widgetId.current); } catch { /* ignore */ }
      }
    }
  }, []);

  // Load + render the invisible Turnstile widget once.
  useEffect(() => {
    if (!TURNSTILE_SITE_KEY || document.getElementById("cf-turnstile-script")) return;
    const script = document.createElement("script");
    script.id = "cf-turnstile-script";
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    // NOTE: do NOT set async/defer here — Cloudflare requires the script to be
    // ready before we render explicitly. We render inside onload (guaranteed loaded).
    script.onload = () => {
      if (!window.turnstile) return;
      try {
        widgetId.current = window.turnstile.render("#ts-contact", {
          sitekey: TURNSTILE_SITE_KEY,
          action: "contact",
          execution: "execute",
          callback: (token) => finishSubmit(token),
          "error-callback": () => finishSubmit(null),
        });
      } catch {
        widgetId.current = null;
      }
    };
    document.body.appendChild(script);
  }, [finishSubmit]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please add your name, email and a short message.");
      return;
    }
    setSending(true);
    settled.current = false;
    // With Turnstile configured, execute() runs the invisible check and the
    // widget callback fires finishSubmit(token). Otherwise submit directly.
    if (TURNSTILE_SITE_KEY && widgetId.current !== null && window.turnstile) {
      try {
        window.turnstile.execute(widgetId.current);
        // Safety net: if the challenge never calls back, submit anyway.
        setTimeout(() => finishSubmit(null), 8000);
      } catch {
        finishSubmit(null);
      }
    } else {
      finishSubmit(null);
    }
  };

  return (
    <section id="contact" className="relative py-28 md:py-44 overflow-hidden" data-testid="contact-section">
      <PaintSplat color="swirl" className="w-[440px] h-[440px] -bottom-16 left-[8%]" rotate={-25} opacity={0.44} from={60} to={-60} />
      <PaintSplat color="coral2" className="w-[440px] h-[440px] -top-20 -right-16" rotate={135} opacity={0.5} from={-50} to={50} />
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-10 items-start">
          {/* Left — heading + big email link */}
          <div className="lg:col-span-6">
            <Reveal className="max-w-xl">
              <span className="font-mono-accent text-[11px] tracking-[0.3em] uppercase text-[#8b8b8b]">
                [ 03 ] &nbsp;Get In Touch
              </span>
              <h2 className="font-display font-semibold tracking-tighter text-6xl md:text-8xl leading-[0.9] mt-6">
                Let's
                <br />
                <span className="text-gold italic">collaborate.</span>
              </h2>
              <p className="text-[#9a9a9a] text-base md:text-lg leading-relaxed mt-8 max-w-lg">
                Got a product to build or a brand to shape? Fill out the form or drop
                us a line — we read every message.
              </p>
            </Reveal>

            <Reveal delay={0.1} className="mt-12">
              <a
                href={`mailto:${EMAIL}`}
                className="group inline-flex items-center gap-3 font-display tracking-tight text-2xl sm:text-3xl md:text-4xl leading-none border-b border-[#333] pb-4 hover:text-gold hover:border-gold transition-colors duration-300 break-all"
                data-testid="contact-email"
              >
                {EMAIL}
                <ArrowUpRight
                  className="shrink-0 text-[#5a5a5a] group-hover:text-gold transition-[color,transform] duration-300"
                  size={30}
                />
              </a>
            </Reveal>
          </div>

          {/* Right — enquiry form */}
          <div className="lg:col-span-6">
            <Reveal delay={0.15}>
              <form
                onSubmit={submit}
                className="rounded-2xl border border-[#242424] bg-[#0f0f11] p-7 md:p-10"
                data-testid="contact-form"
              >
                {/* Honeypot — hidden from humans; bots that fill it get silently dropped */}
                <div aria-hidden="true" className="absolute left-[-9999px] top-auto h-0 w-0 overflow-hidden" style={{ position: "absolute" }}>
                  <label>
                    Website
                    <input
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      value={form.website}
                      onChange={update("website")}
                      data-testid="contact-hp-website"
                    />
                  </label>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <label className="block">
                    <span className="font-mono-accent text-[10px] uppercase tracking-[0.2em] text-[#8b8b8b]">Name</span>
                    <input
                      type="text"
                      value={form.name}
                      onChange={update("name")}
                      placeholder="Jane Appleseed"
                      className={FIELD}
                      data-testid="contact-name-input"
                    />
                  </label>
                  <label className="block">
                    <span className="font-mono-accent text-[10px] uppercase tracking-[0.2em] text-[#8b8b8b]">Email</span>
                    <input
                      type="email"
                      value={form.email}
                      onChange={update("email")}
                      placeholder="jane@company.com"
                      className={FIELD}
                      data-testid="contact-email-input"
                    />
                  </label>
                </div>

                <label className="block mt-6">
                  <span className="font-mono-accent text-[10px] uppercase tracking-[0.2em] text-[#8b8b8b]">Company <span className="text-[#4a4a4a] normal-case tracking-normal">(optional)</span></span>
                  <input
                    type="text"
                    value={form.company}
                    onChange={update("company")}
                    placeholder="Acme Inc."
                    className={FIELD}
                    data-testid="contact-company-input"
                  />
                </label>

                <label className="block mt-6">
                  <span className="font-mono-accent text-[10px] uppercase tracking-[0.2em] text-[#8b8b8b]">Project details</span>
                  <textarea
                    value={form.message}
                    onChange={update("message")}
                    rows={4}
                    placeholder="Tell us what you're building..."
                    className={`${FIELD} resize-none`}
                    data-testid="contact-message-input"
                  />
                </label>

                {/* Invisible Cloudflare Turnstile widget mounts here */}
                <div id="ts-contact" />

                <div className="mt-9">
                  <Magnetic>
                    <button
                      type="submit"
                      disabled={sending}
                      className="inline-flex items-center gap-2 rounded-full bg-gold text-[#050505] px-8 py-4 font-mono-accent text-[12px] uppercase tracking-[0.2em] hover:bg-[#eaeaea] transition-colors duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
                      data-testid="contact-submit"
                    >
                      {sending ? (
                        <>
                          Sending
                          <Loader2 size={16} className="animate-spin" />
                        </>
                      ) : (
                        <>
                          Send Message
                          <ArrowUpRight size={16} />
                        </>
                      )}
                    </button>
                  </Magnetic>
                </div>
              </form>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
